import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BedDouble, Bath, Ruler, MapPin, Car, PawPrint, Sofa, Accessibility, CalendarDays, Building2, ExternalLink, MessageCircleHeart } from "lucide-react";
import { api, fmtRent, bedsLabel } from "../lib/api";
import SEO from "../components/SEO";

export default function ListingDetail() {
  const { id } = useParams();
  const [l, setL] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => setL(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return <div className="max-w-2xl mx-auto py-24 text-center"><h1 className="text-2xl font-bold">Listing not found</h1><Link to="/listings" className="mt-4 inline-block text-primary underline">Browse all homes</Link></div>;
  if (!l) return <div className="max-w-7xl mx-auto py-24 text-center text-muted-foreground">Loading…</div>;

  const title = `${bedsLabel(l)} for rent in ${l.neighborhood}, ${l.city} — ${fmtRent(l.monthly_rent)}/mo`;
  const features = [
    [<BedDouble key="b" className="w-4 h-4" />, bedsLabel(l)],
    [<Bath key="ba" className="w-4 h-4" />, `${l.baths} bathroom${l.baths > 1 ? "s" : ""}`],
    l.square_feet && [<Ruler key="s" className="w-4 h-4" />, `${l.square_feet} ft²`],
    [<Building2 key="bt" className="w-4 h-4" />, l.building_type],
    l.floor && [null, `Floor ${l.floor}`],
    l.year_built && [null, `Built ${l.year_built}`],
    [<Sofa key="f" className="w-4 h-4" />, l.furnished === "na" ? "Furnishing: n/a" : l.furnished],
    [<Car key="p" className="w-4 h-4" />, l.parking === "none" ? "No parking" : l.parking === "included" ? "Parking included" : "Parking available (extra)"],
    [<PawPrint key="pe" className="w-4 h-4" />, { yes: "Pet friendly", no: "No pets", cats_only: "Cats only", dogs_only: "Dogs only", case_by_case: "Pets case-by-case", na: "Pets: n/a" }[l.pet_friendly]],
    [<Accessibility key="a" className="w-4 h-4" />, l.accessibility === "accessible" ? `Accessible${l.accessibility_details ? ` — ${l.accessibility_details}` : ""}` : "Accessibility: n/a"],
    l.date_available && [<CalendarDays key="d" className="w-4 h-4" />, `Available ${l.date_available}`],
    l.laundry !== "na" && [null, { in_unit: "In-suite laundry", shared: "Shared laundry", none: "No laundry" }[l.laundry]],
  ].filter(Boolean);

  return (
    <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-10 md:py-14">
      <SEO title={`${title} | Tendly`} description={(l.description || title).slice(0, 155)}
        jsonLd={{
          "@context": "https://schema.org", "@type": "Apartment", name: title,
          address: { "@type": "PostalAddress", streetAddress: l.hide_exact_address ? undefined : l.address_street, addressLocality: l.city, addressRegion: l.province, addressCountry: "CA" },
          geo: l.latitude ? { "@type": "GeoCoordinates", latitude: l.latitude, longitude: l.longitude } : undefined,
          numberOfRooms: l.beds, floorSize: l.square_feet ? { "@type": "QuantitativeValue", value: l.square_feet, unitCode: "FTK" } : undefined,
          offers: { "@type": "Offer", price: l.monthly_rent, priceCurrency: "CAD" },
        }} />
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-6">
        <Link to="/listings" className="hover:text-primary">Listings</Link> / <Link to={`/rentals/${(l.neighborhood || "").toLowerCase().replace(/[ ,]+/g, "-")}`} className="hover:text-primary">{l.neighborhood}</Link> / <span className="text-foreground">{l.address_street || "Listing"}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <div className="rounded-2xl overflow-hidden border border-border bg-secondary aspect-[4/3]">
            {l.images?.length ? (
              <img src={l.images[imgIdx]?.url} alt={l.images[imgIdx]?.alt || title} className="w-full h-full object-cover" />
            ) : <div className="w-full h-full flex items-center justify-center text-muted-foreground">Photos coming soon</div>}
          </div>
          {l.images?.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto" role="tablist" aria-label="Photo gallery">
              {l.images.map((im, i) => (
                <button key={i} data-testid={`gallery-thumb-${i}`} onClick={() => setImgIdx(i)} aria-label={`Photo ${i + 1}`} aria-selected={i === imgIdx}
                  className={`shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 ${i === imgIdx ? "border-primary" : "border-transparent"}`}>
                  <img src={im.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}

          {l.floor_plan_images?.length > 0 && (
            <div className="mt-10">
              <h2 className="font-heading font-semibold text-xl">Floor plan</h2>
              <div className="mt-4 rounded-2xl overflow-hidden border border-border bg-card p-4 max-w-md">
                <img src={l.floor_plan_images[0]} alt={`Floor plan of ${title}`} className="w-full" loading="lazy" />
              </div>
            </div>
          )}

          <div className="mt-10">
            <h2 className="font-heading font-semibold text-xl">About this home</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">{l.description || "Details coming soon."}</p>
          </div>

          {l.amenities?.length > 0 && (
            <div className="mt-8">
              <h2 className="font-heading font-semibold text-xl">Amenities</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {l.amenities.map((a) => <span key={a} className="rounded-full bg-secondary text-secondary-foreground px-3 py-1.5 text-xs font-medium">{a}</span>)}
              </div>
            </div>
          )}

          {l.sources?.length > 0 && (
            <div className="mt-8">
              <h2 className="font-heading font-semibold text-xl">Also listed on</h2>
              <ul className="mt-3 space-y-2">
                {l.sources.filter((s) => s.source_url).map((s, i) => (
                  <li key={i}>
                    <a data-testid={`source-link-${i}`} href={s.source_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
                      <ExternalLink className="w-3.5 h-3.5" /> {s.source_name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <aside className="lg:col-span-2">
          <div className="lg:sticky lg:top-28 space-y-6">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
              <p className="font-heading font-bold text-3xl tracking-tight">{fmtRent(l.monthly_rent)}<span className="text-base font-medium text-muted-foreground">/mo</span></p>
              <p className="mt-1 text-sm text-muted-foreground flex items-center gap-1.5">
                <MapPin className="w-4 h-4" />
                {l.hide_exact_address ? `${l.neighborhood} (exact address shared after inquiry)` : `${l.address_street}${l.unit_number ? ` #${l.unit_number}` : ""}, ${l.neighborhood}`}
              </p>
              <p data-testid="detail-managed-by" className="mt-2 text-xs text-muted-foreground">
                {l.company_type === "individual_landlord" ? "Listed by" : "Managed by"} <span className="font-semibold text-foreground">{l.company_name || "Independent owner"}</span>
              </p>
              {l.promotions && <p className="mt-3 inline-block rounded-full bg-coral-50 dark:bg-coral-800/30 text-coral-800 dark:text-coral-400 px-3 py-1 text-xs font-semibold">{l.promotions}</p>}
              <Link to="/chat" data-testid="detail-request-viewing"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground py-3 font-semibold hover:bg-coral-600 transition-colors">
                <MessageCircleHeart className="w-4 h-4" /> Request a viewing
              </Link>
              <p data-testid="referral-disclaimer" className="mt-3 text-xs text-muted-foreground leading-relaxed border-t border-border pt-3">Tendly is free for renters. This home is managed by {l.company_name || "the listing company"}.</p>
              <p className="mt-2 text-xs text-muted-foreground text-center">Free for renters · Agent confirms within 24–48h</p>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
              <h2 className="font-heading font-semibold text-lg">Features</h2>
              <ul className="mt-4 space-y-2.5">
                {features.map(([icon, text], i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-muted-foreground">{icon}<span className="text-foreground">{text}</span></li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-sm">
              <div className="h-44 bg-teal-50 dark:bg-teal-900/30 relative flex items-center justify-center" role="img" aria-label={`Map of ${l.neighborhood}`}>
                <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, hsl(var(--primary)) 1px, transparent 0)", backgroundSize: "18px 18px" }} />
                <span className="relative flex flex-col items-center text-primary">
                  <MapPin className="w-8 h-8" />
                  <span className="mt-1 text-xs font-semibold">{l.neighborhood}</span>
                  {l.latitude && <span className="text-[10px] text-muted-foreground font-mono">{l.latitude.toFixed(4)}, {l.longitude.toFixed(4)}</span>}
                </span>
              </div>
              <p className="p-4 text-xs text-muted-foreground">{l.hide_exact_address ? "Approximate location shown — exact address shared after inquiry." : "Location map"}</p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
